import React from "react"

import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn, formatDate } from "@/lib/utils"

import type { User } from "@boundless/bl-api-client/partners/models"

import { Skeleton } from "../ui/skeleton"
import { useUsers } from "./providers/UserProvider"

function UserTableStructure({ children }: { children: React.ReactNode }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Last Login</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Active</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

export function UserTable() {
  const { users, setActive, isUpdatePending } = useUsers()

  const status = (user: User) => {
    if (!user.lastLogin) {
      return "Pending"
    }
    return user.isActive ? "Active" : "Inactive"
  }

  return (
    <UserTableStructure>
      {users.map((user) => (
        <TableRow
          key={user.uuid}
          className={cn("h-10", isUpdatePending && "animate-pulse")}
        >
          <TableCell>
            {user.firstName} {user.lastName}
          </TableCell>
          <TableCell>{user.email}</TableCell>
          <TableCell className="w-32">{formatDate(user.lastLogin)}</TableCell>
          <TableCell className="w-28">{status(user)}</TableCell>
          <TableCell className="w-14 text-center">
            {user.lastLogin ? (
              <Switch
                checked={user.isActive}
                disabled={isUpdatePending}
                onCheckedChange={() => setActive(user.uuid, !user.isActive)}
              />
            ) : null}
          </TableCell>
        </TableRow>
      ))}
    </UserTableStructure>
  )
}

export function FallbackUserTable() {
  return (
    <UserTableStructure>
      {Array.from({ length: 3 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="w-32">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="w-28">
            <Skeleton className="h-6" />
          </TableCell>
          <TableCell className="w-14">
            <Skeleton className="h-6" />
          </TableCell>
        </TableRow>
      ))}
    </UserTableStructure>
  )
}
